// Unified per-page route. One URL: /p/:id.
//
// Architecture mirrors the sandbox harness (see client/src/sandbox/SandboxPage.tsx
// for the full rationale). Three layers:
//
//   PageView (route handler)
//     └── EditableViewerOrReader (gate on canEdit)
//             └── For editors: PageEditor → PageEditorBody key={id}
//                 (BlockNote always-mounted, ref-tracked content, auto-save)
//             └── For viewers: ReadOnlyView (existing BlockRenderer + breadcrumbs)
//
// Why three layers:
//   1. Keying PageEditorBody by pageId guarantees a fresh mount on every nav.
//   2. The query gate ensures the body always starts already-hydrated.
//   3. Editor content lives in a ref written synchronously by editor.onChange,
//      so the unmount-flush always commits the latest document.

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type PageResponse, type TreeJsonResponse, type TreeNodeResponse } from '../lib/api.js';
import { fetchPageContent } from '../lib/content.js';
import { canEdit, getUser } from '../lib/auth.js';
import { useAutoSave, type SaveState } from '../hooks/useAutoSave.js';
import { PageHeader } from '../components/PageHeader.js';
import { PageIcon } from '../components/PageIcon.js';
import { BlockRenderer } from '../components/viewer/BlockRenderer.js';
import { IconPicker } from '../components/editor/IconPicker.js';
import { PageMenu } from '../components/editor/PageMenu.js';

// Lazy-loaded so the BlockNote bundle (~600KB) only ships to editors.
const Editor = lazy(() => import('../components/editor/Editor.js'));

export function PageView() {
  const { id } = useParams<{ id: string }>();
  if (!id) return null;
  const user = getUser();
  if (canEdit(user)) {
    return <PageEditor key={id} pageId={id} />;
  }
  return <ReadOnlyView key={id} pageId={id} />;
}

// ─── Editor (gates on the page query, then mounts the body) ──────────────────

function PageEditor({ pageId }: { pageId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['page', pageId],
    queryFn: () => api.getPage(pageId),
    staleTime: 0,
  });

  if (isLoading) return <div className="p-12 text-brand-500">Loading…</div>;
  if (error || !data) {
    return (
      <div className="p-12">
        <h1 className="text-2xl mb-4 font-display">Page not found</h1>
        <Link to="/" className="underline text-brand-600">Back home</Link>
      </div>
    );
  }
  if (data.page.id !== pageId) {
    return <div className="p-12 text-brand-500">Loading…</div>;
  }
  return <PageEditorBody key={pageId} pageId={pageId} initial={data.page} />;
}

interface PageEditorBodyProps {
  pageId: string;
  initial: PageResponse;
}

function PageEditorBody({ pageId, initial }: PageEditorBodyProps) {
  const queryClient = useQueryClient();

  const [title, setTitle] = useState<string>(initial.title ?? '');
  const [icon, setIcon] = useState<string | null>(initial.icon ?? null);
  const contentRef = useRef<unknown>(initial.content);
  const [contentRevision, setContentRevision] = useState(0);

  const onContentChange = useCallback(() => {
    setContentRevision((v) => v + 1);
  }, []);

  const { data: tree } = useQuery({
    queryKey: ['tree'],
    queryFn: () => api.getTree(),
  });

  const save = useCallback(
    async (snapshot: { title: string; icon: string | null; contentRevision: number }) => {
      const res = await api.updatePage(pageId, {
        title: snapshot.title,
        icon: snapshot.icon,
        content: contentRef.current,
      });
      queryClient.setQueryData<TreeJsonResponse>(['tree'], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: prev.pages.map((p) =>
            p.id === pageId
              ? { ...p, title: snapshot.title, icon: snapshot.icon, v: res.version }
              : p,
          ),
        };
      });
      queryClient.setQueryData<{ page: PageResponse }>(['page', pageId], { page: res.page });
    },
    [pageId, queryClient],
  );

  const autoSaveValue = useMemo(
    () => ({ title, icon, contentRevision }),
    [title, icon, contentRevision],
  );

  const saveState = useAutoSave({
    value: autoSaveValue,
    save,
    delay: 600,
    skipInitial: true,
  });

  const breadcrumbs = useMemo(() => {
    if (!tree) return [];
    const byId = new Map(tree.pages.map((p) => [p.id, p]));
    const chain: TreeNodeResponse[] = [];
    let cursor: TreeNodeResponse | undefined = byId.get(pageId);
    const seen = new Set<string>();
    while (cursor) {
      if (seen.has(cursor.id)) break;
      seen.add(cursor.id);
      chain.unshift(cursor);
      cursor = cursor.parent ? byId.get(cursor.parent) : undefined;
    }
    return chain;
  }, [tree, pageId]);

  const children = useMemo(() => {
    if (!tree) return [];
    return tree.pages
      .filter((p) => p.parent === pageId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [tree, pageId]);

  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const iconWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!iconPickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!iconWrapperRef.current?.contains(e.target as Node)) {
        setIconPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [iconPickerOpen]);

  return (
    <article>
      <div className="px-12 pt-6 pb-2 max-w-4xl mx-auto flex items-center justify-between gap-4">
        {breadcrumbs.length > 1 ? (
          <Breadcrumbs crumbs={breadcrumbs} />
        ) : (
          <span />
        )}
        <div className="flex items-center gap-3 shrink-0">
          <SaveIndicator state={saveState} />
          <PageMenu pageId={pageId} />
        </div>
      </div>

      <div className="px-12 pt-6 pb-4 max-w-4xl mx-auto">
        <div ref={iconWrapperRef} className="relative inline-block mb-2">
          <button
            type="button"
            onClick={() => setIconPickerOpen((o) => !o)}
            aria-label="Change page icon"
            title="Change page icon"
            className="block leading-none p-1 -ml-1 hover:bg-brand-50 transition-colors"
          >
            {icon ? (
              <span className="text-5xl inline-block">
                <PageIcon icon={icon} size="xl" alt="" />
              </span>
            ) : (
              <span className="text-sm text-brand-400 hover:text-brand-700 inline-flex items-center gap-1 px-2 py-1">
                ＋ Add icon
              </span>
            )}
          </button>
          {iconPickerOpen && (
            <IconPicker
              value={icon}
              onChange={setIcon}
              onClose={() => setIconPickerOpen(false)}
            />
          )}
        </div>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const editorRoot = document.querySelector<HTMLElement>(
                '.bn-host [contenteditable="true"]',
              );
              editorRoot?.focus();
            }
          }}
          placeholder="Untitled"
          className="text-4xl font-display font-bold text-brand-800 outline-none w-full bg-transparent placeholder:text-brand-200"
          aria-label="Page title"
        />
      </div>

      <Suspense fallback={<div className="p-12 text-brand-500">Loading editor…</div>}>
        <Editor
          parentPageId={pageId}
          initialContent={contentRef.current}
          contentRef={contentRef}
          onContentChange={onContentChange}
        />
      </Suspense>

      {children.length > 0 && (
        <section className="px-12 pb-24 max-w-4xl mx-auto">
          <h2 className="text-xs uppercase tracking-wider text-brand-500 mb-3 mt-2 font-display">
            Sub-pages ({children.length})
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
            {children.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/p/${c.id}`}
                  className="flex items-center gap-2 px-2 py-1 hover:bg-brand-50 text-brand-700 truncate"
                >
                  <PageIcon icon={c.icon} size="md" alt="" fallback="dot" />
                  <span className="truncate">{c.title || 'Untitled'}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

// ─── Read-only view (viewers) ────────────────────────────────────────────────
// Same path as the prior PageView: tree-resolved version → immutable
// /pages/<id>/<v>.json fetched from CloudFront. No Lambda, no editor bundle.

function ReadOnlyView({ pageId }: { pageId: string }) {
  const { data: tree } = useQuery({
    queryKey: ['tree'],
    queryFn: () => api.getTree(),
  });

  const treeNode = useMemo(
    () => tree?.pages.find((p) => p.id === pageId),
    [tree, pageId],
  );

  const breadcrumbs = useMemo(() => {
    if (!tree || !treeNode) return [];
    const byId = new Map(tree.pages.map((p) => [p.id, p]));
    const chain: TreeNodeResponse[] = [];
    let cursor: TreeNodeResponse | undefined = treeNode;
    const seen = new Set<string>();
    while (cursor) {
      if (seen.has(cursor.id)) break;
      seen.add(cursor.id);
      chain.unshift(cursor);
      cursor = cursor.parent ? byId.get(cursor.parent) : undefined;
    }
    return chain;
  }, [tree, treeNode]);

  const children = useMemo(() => {
    if (!tree || !treeNode) return [];
    return tree.pages
      .filter((p) => p.parent === treeNode.id)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [tree, treeNode]);

  const { data: page, isLoading, error } = useQuery({
    queryKey: ['page-content', pageId, treeNode?.v],
    queryFn: () => fetchPageContent(pageId, treeNode!.v),
    enabled: !!treeNode,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  if (!tree) return <div className="p-12 text-brand-500">Loading…</div>;
  if (!treeNode) {
    return (
      <div className="p-12">
        <h1 className="text-2xl mb-4 font-display">Page not found</h1>
        <Link to="/" className="underline text-brand-600">Back home</Link>
      </div>
    );
  }
  if (isLoading) return <div className="p-12 text-brand-500">Loading…</div>;
  if (error || !page) {
    return (
      <div className="p-12">
        <h1 className="text-2xl mb-4 font-display">Failed to load page</h1>
        <pre className="text-sm text-red-700">{String(error)}</pre>
      </div>
    );
  }

  return (
    <article>
      <div className="px-12 pt-6 pb-2 max-w-4xl mx-auto flex items-center justify-between">
        {breadcrumbs.length > 1 ? <Breadcrumbs crumbs={breadcrumbs} /> : <span />}
      </div>
      <PageHeader title={page.title} icon={page.icon} cover={page.cover} />
      <BlockRenderer content={page.content} />
      {children.length > 0 && (
        <section className="px-12 pb-24 max-w-4xl mx-auto">
          <h2 className="text-sm uppercase tracking-wider text-brand-500 mb-3 mt-8 font-display">
            Sub-pages ({children.length})
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
            {children.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/p/${c.id}`}
                  className="flex items-center gap-2 px-2 py-1 hover:bg-brand-50 text-brand-700 truncate"
                >
                  <PageIcon icon={c.icon} size="md" alt="" fallback="dot" />
                  <span className="truncate">{c.title || 'Untitled'}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

// ─── Shared bits ──────────────────────────────────────────────────────────────

function Breadcrumbs({ crumbs }: { crumbs: TreeNodeResponse[] }) {
  return (
    <nav className="text-sm text-brand-500 flex flex-wrap items-center gap-1">
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={crumb.id} className="flex items-center gap-1">
            {i > 0 && <span className="text-brand-300">/</span>}
            {isLast ? (
              <span className="text-brand-700 inline-flex items-center gap-1">
                <PageIcon icon={crumb.icon} size="sm" alt="" />
                <span>{crumb.title || 'Untitled'}</span>
              </span>
            ) : (
              <Link
                to={`/p/${crumb.id}`}
                className="hover:text-brand-700 hover:underline inline-flex items-center gap-1"
              >
                <PageIcon icon={crumb.icon} size="sm" alt="" />
                <span>{crumb.title || 'Untitled'}</span>
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}

function SaveIndicator({ state }: { state: SaveState }) {
  const label = {
    idle: '',
    pending: 'Unsaved…',
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Save failed',
  }[state];
  const color =
    state === 'error'
      ? 'text-red-700'
      : state === 'saved'
        ? 'text-green-700'
        : 'text-brand-500';
  return <span className={`text-xs ${color}`}>{label}</span>;
}
